'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Landmark } from 'lucide-react';
import { useStyles } from '@/shared/lib/hooks/useStyles';
import {
  useClassifiedProjectReferences,
  useProjectReferences,
} from '@/features/project-reference/hooks/use-project-references';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import { StyleContent } from '../settings-content/StyleContent';
import { SettingsSectionRow } from './SettingsSection';
import { ReferenceSummary, ThumbStrip } from './ReferenceSummary';
import { assistantTargets, sectionTarget } from '@/shared/assistant';

interface StyleSectionProps {
  selectedStyleIds: number[] | null;
  onStyleChange: (styleIds: number[] | null) => void;
  hasChanges?: boolean;
  projectId?: string;
  styleProjectReferenceIds?: string[] | null;
  onStyleProjectReferencesChange?: (ids: string[] | null) => void;
  useAutoStyleRefs?: boolean;
  onUseAutoStyleRefsChange?: (value: boolean) => void;
  onNavigateToReferences?: () => void;
  isRefine?: boolean;
}

export function StyleSection({
  selectedStyleIds,
  onStyleChange,
  hasChanges = false,
  projectId,
  styleProjectReferenceIds,
  onStyleProjectReferencesChange,
  useAutoStyleRefs,
  onUseAutoStyleRefsChange,
  onNavigateToReferences,
  isRefine = false,
}: StyleSectionProps) {
  const t = useTranslations('render.settings.style');
  const { groupedStyles } = useStyles();
  const { data: categoryRefs = [] } = useClassifiedProjectReferences(
    projectId ?? '',
    'style',
    { enabled: !!projectId }
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const allStyles = [
    ...groupedStyles.contemporary,
    ...groupedStyles.traditional,
  ];
  const selectedStyleImages = (selectedStyleIds ?? [])
    .map((id) => allStyles.find((s) => parseInt(s.id) === id)?.attributes.image_url)
    .filter((url): url is string => !!url);

  // Selected references resolve by id across ALL references (any category):
  // the picker lets users select refs from other/unclassified categories, so a
  // category-scoped lookup dropped their thumbnails and the row wrongly showed
  // "no refs". Auto mode (allRefImages) stays category-scoped below.
  const { data: allRefs = [] } = useProjectReferences(projectId ?? '', {
    enabled: !!projectId,
  });
  const refImageUrl = (id: string) =>
    allRefs.find((r) => r.id === id)?.attributes.file_url ?? null;
  const selectedRefImages = (styleProjectReferenceIds ?? [])
    .map(refImageUrl)
    .filter((url): url is string => !!url);
  const allRefImages = categoryRefs
    .map((r) => r.attributes.file_url)
    .filter((url): url is string => !!url);

  // Library styles selected → square icons (as-is); otherwise the references
  // summary (circles / "Auto" + circles / "No refs").
  const subtitleContent =
    selectedStyleImages.length > 0 ? (
      <ThumbStrip images={selectedStyleImages} shape="square" invert />
    ) : (
      <ReferenceSummary
        selectedRefImages={selectedRefImages}
        allRefImages={allRefImages}
        useAutoRefs={useAutoStyleRefs ?? true}
        isRefine={isRefine}
        autoLabel={t('auto')}
      />
    );

  return (
    <>
      <SettingsSectionRow
        assistantTarget={assistantTargets(sectionTarget('style'))}
        icon={Landmark}
        title={t('title')}
        subtitleContent={subtitleContent}
        hasChanges={hasChanges}
        onClick={() => setIsSheetOpen(true)}
      />

      <NestedSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('title')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <StyleContent
              selectedStyleIds={selectedStyleIds}
              onStyleChange={onStyleChange}
              projectId={projectId}
              styleProjectReferenceIds={styleProjectReferenceIds}
              onStyleProjectReferencesChange={onStyleProjectReferencesChange}
              useAutoStyleRefs={useAutoStyleRefs}
              onUseAutoStyleRefsChange={onUseAutoStyleRefsChange}
              onNavigateToReferences={onNavigateToReferences}
              variant="compact"
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
    </>
  );
}
